import React, { useCallback, useEffect, useMemo } from 'react';

import cn from 'classnames';
import { MdNotificationsNone, MdNotificationsOff } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { programSel } from '@context/duck/selectorsTyped';
import { useUnitPendingLeadershipPositions } from '@modules/leadershipPositions/hooks';
import { useGetOrgAdvancementsDashboardQuery } from '@modules/rtk/esb-api';
import { organizationTypeId } from '@modules/shared';
import {
  setNotificationsVisible,
  setValue,
} from '@modules/userNotifications/duck/slice';
import { computeNotificationsCount } from '@modules/userNotifications/utils';
import { Badge, Modal, Popover } from '@shared/components';
import {
  isParentOrYouthMemberSel,
  isScoutbookRoleSel,
  organizationGuidSel,
  selectedOrganizationSel,
} from '@shared/duck';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';

import { notificationsVisibleSel } from '../../duck';
import styles from './Notifications.less';
import { NotificationsModalContentV2 } from './NotificationsContentV2';
import NotificationsModalContent from './NotificationsModalContent';

interface NotificationsProps {
  className?: string;
  visible: boolean;
  count: number;
  isMobile: boolean;
  isParentOryouthMember: boolean;
  isScoutbookRole: boolean;
  onSetVisible: (visible: boolean) => void;
}

const Notifications: React.FC<NotificationsProps> = props => {
  const { isMobile, visible, count, isParentOryouthMember, isScoutbookRole } =
    props;

  if (isParentOryouthMember || isScoutbookRole) {
    return null;
  }

  const handleVisibleChange = (visible: boolean) => {
    props.onSetVisible(visible);
  };

  const handleOpen = () => {
    if (props.count > 0) {
      handleVisibleChange(true);
    }
  };

  const renderIcon = () => {
    const { count, className } = props;
    return (
      <div
        className={cn(styles.wrapper, className, {
          [styles.noNotifications]: count == 0,
        })}
        onClick={handleOpen}
      >
        <Badge count={count} className={styles.badgeCount}>
          {count > 0 ? (
            <MdNotificationsNone className={styles.notificationIcon} />
          ) : (
            <MdNotificationsOff className={styles.notificationsOffIcon} />
          )}
        </Badge>
      </div>
    );
  };

  const handleClose = () => handleVisibleChange(false);

  if (!isMobile) {
    return (
      <span className={styles.popoverParent}>
        <Popover
          noArrow
          arrowPointAtCenter
          content={
            count > 0 ? (
              <NotificationsModalContentV2 />
            ) : (
              <div className={styles.noNotificationsContent}>
                <FormattedMessage id="userNotifications.Notifications.noNotificationsMessage" />
              </div>
            )
          }
          trigger="click"
          placement="bottomRight"
          visible={visible}
          getPopupContainer={({ parentNode }) => parentNode as HTMLElement}
          onVisibleChange={handleVisibleChange}
        >
          {renderIcon()}
        </Popover>
      </span>
    );
  } else {
    return (
      <React.Fragment>
        <Modal mask={false} visible={visible} onCancel={handleClose}>
          <NotificationsModalContent onClose={handleClose} />
        </Modal>
        {renderIcon()}
      </React.Fragment>
    );
  }
};

const NotificationsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useSelector(isMobileSel);
  const { pendingCount: pendingPositionsCount } =
    useUnitPendingLeadershipPositions();
  const visible = useSelector(notificationsVisibleSel);
  const isParentOryouthMember = useSelector(isParentOrYouthMemberSel);
  const isScoutbookRole = useSelector(isScoutbookRoleSel);
  const orgGuid = useSelector(organizationGuidSel);
  const unitProgram = useSelector(programSel);
  const { organizationTypeId: currentOrgTypeId } = useSelector(
    selectedOrganizationSel,
  );

  const { data: stats } = useGetOrgAdvancementsDashboardQuery(
    { organizationGuid: orgGuid },
    {
      skip: !orgGuid || Number(currentOrgTypeId) !== organizationTypeId.UNIT,
    },
  );

  const onSetVisible = useCallback(
    (visible: boolean) => dispatch(setNotificationsVisible(visible)),
    [dispatch],
  );

  const counts = useMemo(
    () =>
      computeNotificationsCount({
        stats,
        positionsCount: pendingPositionsCount,
        unitProgram,
      }),
    [stats, pendingPositionsCount, unitProgram],
  );

  useEffect(() => {
    dispatch(
      setValue({
        dashboardStats: stats,
        toPurchaseCount: counts.toPurchaseCount,
        toApproveCount: counts.toApproveCount,
        positionsCount: counts.positionsCount,
        totalCount: counts.totalCount,
      }),
    );
  }, [dispatch, stats, counts]);

  return (
    <Notifications
      onSetVisible={onSetVisible}
      isMobile={isMobile}
      count={counts.totalCount}
      visible={visible}
      isParentOryouthMember={isParentOryouthMember}
      isScoutbookRole={isScoutbookRole}
    />
  );
};

export default NotificationsContainer;
